import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {NgClass, NgFor, NgIf, NgStyle} from '@angular/common';
import {
  CardQuestionnaireOptionDto,
  InteractionAnswerSaveDto,
  InteractionCardDto,
  InteractionsCardItemDto,
  ResultsDto, ResultsItemDto, StatisticsDto
} from '../../models/card.model';
import {CardButtonComponent} from '../card-button/card-button.component';
import {CardResultsComponent} from '../card-results/card-results.component';
import {ConferenceService} from '../../service/conference.service';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-card-rating',
  templateUrl: './card-rating.component.html',
  styleUrls: ['./card-rating.component.scss'],
  standalone: true,
  imports: [NgStyle, NgClass, NgIf, NgFor, CardButtonComponent, CardResultsComponent, TranslateModule],
})
export class CardRatingComponent implements OnInit {
  @Output() onSubmit = new EventEmitter<InteractionAnswerSaveDto>();

  @Input() set card(card: InteractionCardDto) {
    this._card = card;
    this.isSubmitted = (card.cardAnswer && card.cardAnswer.isCompleted);

    this.type = this._card.type;
    this.ratingType = this._card.ratingType;
    this.minSelect = this._card.minSelect;
    this.maxSelect = this._card.maxSelect;
    this.comment = this._card.comment;
    this.color = this._card.color;

    if (this._card.interactionsCardItems && this._card.interactionsCardItems.length) {
      this.options = this._card.interactionsCardItems.map(item => this.extractInteractionAnswer(item));
    }

    this.switchViews();
  }

  @Input() set isResults(isResults: boolean) {
    this._isResults = isResults;
    this.switchViews();
  }

  type: string = 'rating'; // quiz, poll
  ratingType: string = 'stars'; //stars, buttons
  minSelect: number = 0; // optional
  maxSelect: number = 0; // optional
  comment: string = ''; // optional
  color: string = ''; // optional

  isEnableSwitchViews: boolean = false;
  isSubmitted: boolean = false;

  _isResults: boolean | null = null;
  isShowingResults: boolean | null = null;

  range: number[] = [];
  rangeMin: number = 1;
  rangeMax: number = 5;

  _card: InteractionCardDto = {} as InteractionCardDto;
  options: CardQuestionnaireOptionDto[] = [];
  optionsStyles: any = {};
  results: ResultsDto | null = null;

  constructor(
    private conferenceService: ConferenceService,
  ) {
  }

  ngOnInit() {
    this.initRange();
  }

  initRange() {
    this.rangeMin = this.minSelect > 0 ? this.minSelect : 1;
    this.rangeMax = this.maxSelect > 0 ? this.maxSelect : 5;

    for (let i = this.rangeMin; i <= this.rangeMax; i++) {
      this.range.push(i);
    }

    this.setOptionsStyles(this.range.length);
  }

  setOptionsStyles(column: number) {
    this.optionsStyles = {'grid-template-columns': 'repeat(' + column + ', minmax(3rem, 1fr))'};
  }

  choose(option: CardQuestionnaireOptionDto, value: any) {
    if (this.isSubmitted) {
      return;
    }

    option.isSelected = true;
    option.value = value;
  }

  isValid() {
    return this.options.every(o => o.isSelected);
  }

  submit() {
    if (!this.isValid()) {
      return;
    }

    this.isSubmitted = true;
    this.onSubmit.emit({
      interactionCardId: this._card.id,
      score: this._card.score,
      isCompleted: true,
      isPassed: true,
      groupExtId: 0,
      text: '',
      InteractionAnswerItems: this.options.map(o => ({
        interactionsCardItemId: o.item.id,
        value: o.isSelected ? o.value : 0,
        score: o.item.score,
      })),
    } as InteractionAnswerSaveDto);
  }

  private extractInteractionAnswer(item: any) {
    return {
      isSelected: item.cardItemAnswer && item.cardItemAnswer.value > 0 ? true : false,
      value: item.cardItemAnswer && item.cardItemAnswer.value > 0 ? item.cardItemAnswer.value : 0,
      item
    };
  }

  switchViews() {
    if (this._isResults === null) {
      return;
    }

    if (this._isResults) {
      if (this.ratingType === 'stars') {
        this.showResultsExt();
      } else {
        this.showResults();
      }
    } else {
      this.hideResults();
    }
  }

  showResults() {
    this.conferenceService.getStatistics(this._card.id, 0)
      .then((statistics) => {
        console.log('statistics', statistics);

        this.results = {
          items: this.options.map((option) => {
            const percent = this.getPercents(option.item.id, statistics);
            return {
              value: percent / 100,
              label: this.getLabelExt(option.item.id, statistics),
              title: option.item.text,
              status: this.getResultsItemStyle(option.item),
            } as ResultsItemDto;
          })
        };

        this.isShowingResults = true;
      });
  }

  showResultsExt() {
    this.conferenceService.getStatisticsExt(this._card.id, 0)
      .then((statistics) => {
        console.log('statistics', statistics);

        this.results = {
          range: Array.from({length: this._card.maxSelect - this._card.minSelect + 1}, (v, k) => (this._card.minSelect + k).toString()),
          items: this.options.map((option) => {
            const percent = this.getPercents(option.item.id, statistics);
            return {
              value: percent / 100,
              label: this.getLabelExt(option.item.id, statistics),
              title: option.item.text,
              status: this.getResultsItemStyle(option.item),
              options: this.parseResultsExt(option.item.id, statistics)
            } as ResultsItemDto;
          })
        };

        this.isShowingResults = true;
      });
  }

  hideResults() {
    this.isShowingResults = false;
  }

  getResultsItemStyle(item: InteractionsCardItemDto) {
    return 'neutral';
  }

  getPercents(itemId: string, statistics: StatisticsDto) {
    if (statistics && statistics.interactionCardAnswerItems && statistics.interactionCardAnswerItems.length > 0) {
      const item = statistics.interactionCardAnswerItems.find((item) => item.interactionsCardItemId === itemId);
      const total = this._card.maxSelect > 0 ? this._card.maxSelect : 1;
      return total > 0 && item ? parseFloat(item.InteractionAnswerItemsAvg) / total * 100 : 0;
    }
    return 0;
  }

  getLabelExt(itemId: string, statistics: StatisticsDto) {
    if (statistics && statistics.interactionCardAnswerItems && statistics.interactionCardAnswerItems.length > 0) {
      const item: any = statistics.interactionCardAnswerItems.find((item) => item.interactionsCardItemId === itemId);
      return parseFloat(item.InteractionAnswerItemsAvg).toFixed(2) + ' / ' + this._card.maxSelect;
    }
    return '0 / ' + this._card.maxSelect;
  }

  parseResultsExt(itemId: string, statistics: StatisticsDto) {
    return Array.from({length: this._card.maxSelect - this._card.minSelect + 1}, (v, k) => {
      const optionId = k + this._card.minSelect;
      const percent = this.getOptionPercents(itemId, optionId, statistics);

      return {
        value: percent / 100,
        label: percent.toFixed(0) + '%',
        title: optionId.toString(),
        status: 'neutral',
      };
    }).reverse();
  }

  getOptionPercents(itemId: string, optionId: number, statistics: StatisticsDto) {
    if (statistics && statistics.interactionCardAnswerItems && statistics.interactionCardAnswerItems.length > 0
      && statistics.interactionCardAnswerItemsValues && statistics.interactionCardAnswerItemsValues.length > 0) {
      const item: any = statistics.interactionCardAnswerItems.find((item) => item.interactionsCardItemId === itemId);
      const total = item.InteractionAnswerItemsCount;
      const option = statistics.interactionCardAnswerItemsValues.find((item) => item.interactionsCardItemId === itemId && item.value === optionId);
      if (option && total > 0) {
        return option.InteractionAnswerItemsCount / total * 100;
      }
    }
    return 0;
  }
}
